import React, { useState } from 'react';
import { Row, Col, Empty, Button, Space, Typography, Segmented, Table as AntTable, Tag, Dropdown, MenuProps } from 'antd';
import {
  PlusOutlined,
  TableOutlined,
  AppstoreOutlined,
  UnorderedListOutlined,
  DotChartOutlined,
  MoreOutlined,
  EditOutlined,
  DeleteOutlined,
  PlayCircleOutlined,
  StopOutlined,
  SwapOutlined,
  MergeCellsOutlined,
  SplitCellsOutlined,
  LinkOutlined,
} from '@ant-design/icons';
import TableCard from './TableCard';
import TableFloorView from './TableFloorView';
import { Table, TableStatus } from '../../types/tables';
import { useResponsive } from '../../hooks/useResponsive';
import { useAppSelector } from '../../hooks/redux';
import { isAdmin, isManager } from '../../utils/roleHelpers';
import {
  statusColorMap,
  statusLabelMap,
  getStatusIcon,
  formatElapsedTime,
  formatMoneyCompact,
  FloorStats,
} from './tableHelpers';

const { Title } = Typography;

type TablesViewMode = 'cards' | 'list' | 'floor';
const VIEW_MODES: TablesViewMode[] = ['cards', 'list', 'floor'];
// Por dispositivo: la tablet del mesero y la caja pueden preferir vistas distintas.
const VIEW_MODE_KEY = 'tables.viewMode';

const readViewMode = (): TablesViewMode => {
  try {
    const saved = localStorage.getItem(VIEW_MODE_KEY) as TablesViewMode | null;
    return saved && VIEW_MODES.includes(saved) ? saved : 'cards';
  } catch {
    return 'cards';
  }
};

interface TableGridProps {
  tables: Table[];
  areaName?: string;
  selectedTableId: string | null;
  onTableSelect: (table: Table) => void;
  onTableEdit?: (table: Table) => void;
  onTableDelete?: (tableId: string) => void;
  onTableOpen?: (table: Table) => void;
  onTableClose?: (tableId: string) => void;
  onTableTransfer?: (table: Table) => void;
  onTableMerge?: (table: Table) => void;
  onTableSplit?: (table: Table) => void;
  onTableStatusChange?: (tableId: string, status: TableStatus) => void;
  onAddTable?: () => void;
  hasAreas?: boolean;
  loading?: boolean;
  showAreaColumn?: boolean;
  /** Floor-wide counters shown as chips in the header (always global, not area-filtered). */
  floorStats?: FloorStats;
}

const TableGrid: React.FC<TableGridProps> = ({
  tables,
  areaName,
  selectedTableId,
  onTableSelect,
  onTableEdit,
  onTableDelete,
  onTableOpen,
  onTableClose,
  onTableTransfer,
  onTableMerge,
  onTableSplit,
  onTableStatusChange,
  onAddTable,
  hasAreas = true,
  loading = false,
  showAreaColumn = false,
  floorStats,
}) => {
  const { isMobile, isTablet } = useResponsive();
  const { user } = useAppSelector((state) => state.auth);
  const hasAdminAccess = isAdmin(user?.role) || isManager(user?.role);
  const [viewMode, setViewModeState] = useState<TablesViewMode>(readViewMode);
  const setViewMode = (mode: TablesViewMode) => {
    setViewModeState(mode);
    try {
      localStorage.setItem(VIEW_MODE_KEY, mode);
    } catch {
      // Sin almacenamiento (modo privado) la vista dura lo que la sesión.
    }
  };

  const handleRowClick = (table: Table) => {
    if (table.status === 'Available' || table.status === 'Reserved') {
      onTableOpen?.(table);
    } else {
      onTableSelect(table);
    }
  };

  const getRowMenuItems = (table: Table): MenuProps['items'] => {
    const isMergedSecondary = !!table.mergedIntoTableId;
    const isMergedPrimary = (table.mergedTables?.length ?? 0) > 0;
    return [
    ...(table.status === 'Available' && !isMergedSecondary ? [{
      key: 'reserve',
      label: 'Reservar Mesa',
      icon: <SwapOutlined />,
      onClick: () => onTableStatusChange?.(table.id, 'Reserved'),
    }] : []),
    ...(table.status === 'Reserved' && !isMergedSecondary ? [{
      key: 'cancel-reservation',
      label: 'Cancelar Reserva',
      icon: <StopOutlined />,
      onClick: () => onTableStatusChange?.(table.id, 'Available'),
    }] : []),
    ...((table.status === 'Occupied' || table.status === 'PreCheck') && !isMergedSecondary && !isMergedPrimary ? [{
      key: 'transfer',
      label: 'Transferir Mesa',
      icon: <SwapOutlined />,
      onClick: () => onTableTransfer?.(table),
    }] : []),
    ...(!isMergedSecondary && (table.status === 'Available' || table.status === 'Reserved' || table.status === 'Occupied') ? [{
      key: 'merge',
      label: isMergedPrimary ? 'Unir Otra Mesa' : 'Unir Mesas',
      icon: <MergeCellsOutlined />,
      onClick: () => onTableMerge?.(table),
    }] : []),
    ...(isMergedPrimary ? [{
      key: 'split',
      label: 'Separar Mesas',
      icon: <SplitCellsOutlined />,
      onClick: () => onTableSplit?.(table),
    }] : []),
    ...(table.status === 'Dirty' ? [{
      key: 'available',
      label: 'Liberar Mesa',
      icon: <PlayCircleOutlined />,
      onClick: () => onTableStatusChange?.(table.id, 'Available'),
    }] : []),
    ...(hasAdminAccess ? [
      { type: 'divider' as const },
      {
        key: 'edit',
        label: 'Editar Mesa',
        icon: <EditOutlined />,
        onClick: () => onTableEdit?.(table),
      },
      {
        key: 'delete',
        label: 'Eliminar Mesa',
        icon: <DeleteOutlined />,
        danger: true,
        disabled: table.status === 'Occupied' || table.status === 'PreCheck' || isMergedSecondary || isMergedPrimary,
        onClick: () => onTableDelete?.(table.id),
      },
    ] : []),
    ];
  };

  const listColumns = [
    {
      title: 'Mesa',
      dataIndex: 'number',
      key: 'number',
      width: 100,
      render: (number: string) => <span style={{ fontWeight: 600, fontSize: 15 }}>{number}</span>,
    },
    ...(showAreaColumn ? [{
      title: 'Área',
      dataIndex: 'areaName',
      key: 'areaName',
      width: 150,
    }] : []),
    {
      title: 'Personas',
      key: 'capacity',
      width: 110,
      render: (_: unknown, record: Table) =>
        record.guestCount ? `${record.guestCount}/${record.capacity}` : `${record.capacity}`,
    },
    {
      title: 'Estado',
      dataIndex: 'status',
      key: 'status',
      width: 140,
      render: (status: TableStatus, record: Table) =>
        record.mergedIntoTableId ? (
          <Tag icon={<LinkOutlined />} color="error">
            Unida a M{record.mergedIntoTableNumber}
          </Tag>
        ) : (
          <Tag
            icon={getStatusIcon(status)}
            color={statusColorMap[status]}
          >
            {statusLabelMap[status]}
          </Tag>
        ),
    },
    {
      title: 'Tiempo',
      key: 'elapsed',
      width: 110,
      render: (_: unknown, record: Table) =>
        record.status === 'Occupied' ? formatElapsedTime(record.lastActivity) : '—',
    },
    {
      title: 'Mesero',
      key: 'waiter',
      width: 140,
      render: (_: unknown, record: Table) => record.assignedWaiterName || '—',
    },
    {
      title: 'Total',
      key: 'total',
      width: 100,
      align: 'right' as const,
      render: (_: unknown, record: Table) =>
        record.currentOrderTotal != null ? formatMoneyCompact(record.currentOrderTotal) : '—',
    },
    {
      title: '',
      key: 'actions',
      width: 50,
      render: (_: unknown, record: Table) => (
        <Dropdown
          menu={{ items: getRowMenuItems(record) }}
          trigger={['click']}
          placement="bottomRight"
        >
          <Button
            type="text"
            icon={<MoreOutlined />}
            size="small"
            onClick={(e) => e.stopPropagation()}
          />
        </Dropdown>
      ),
    },
  ];

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px 0' }}>
        Cargando mesas...
      </div>
    );
  }

  if (tables.length === 0) {
    return (
      <Empty
        image={<TableOutlined style={{ fontSize: 64, color: '#d9d9d9' }} />}
        description={
          <Space direction="vertical" size="middle">
            <Title level={4} type="secondary">
              No hay mesas en esta área
            </Title>
            {onAddTable && hasAdminAccess && (
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={onAddTable}
                size="large"
                disabled={!hasAreas}
                title={!hasAreas ? 'Debe crear al menos un área primero' : undefined}
              >
                Agregar Primera Mesa
              </Button>
            )}
          </Space>
        }
      />
    );
  }

  return (
    <div>
      {/* Single bar: area name + count + floor pulse + view toggle + add button.
          The status legend is gone — tinted cards with labeled badges carry it. */}
      <div className="table-grid-header">
        <div className="table-grid-header-left">
          <h3>{areaName || 'Mesas'}</h3>
          <span className="table-count-badge">{tables.length} Mesas</span>
        </div>

        <div className="table-grid-header-right">
          {floorStats && (
            <div className="floor-pulse">
              <span className="floor-pulse-chip">
                <span className="floor-pulse-dot occupied" />
                <b>{floorStats.occupied}</b> ocupadas
              </span>
              <span className="floor-pulse-chip">
                <span className="floor-pulse-dot precheck" />
                <b>{floorStats.preCheck}</b> por cobrar
              </span>
              <span className="floor-pulse-chip">
                <span className="floor-pulse-dot available" />
                <b>{floorStats.available}</b> disponibles
              </span>
              {/* Floor-wide money is management info — staff see per-table totals only */}
              {hasAdminAccess && floorStats.openTotal > 0 && (
                <span className="floor-pulse-chip">
                  en mesas <b>{formatMoneyCompact(floorStats.openTotal)}</b>
                </span>
              )}
            </div>
          )}

          <Segmented
            size={isMobile ? 'small' : 'middle'}
            value={viewMode}
            onChange={(val) => setViewMode(val as TablesViewMode)}
            options={[
              { value: 'cards', icon: <AppstoreOutlined />, title: 'Tarjetas' },
              { value: 'list', icon: <UnorderedListOutlined />, title: 'Lista' },
              { value: 'floor', icon: <DotChartOutlined />, title: 'Compacta' },
            ]}
          />

          {onAddTable && hasAdminAccess && (
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={onAddTable}
              disabled={!hasAreas}
              title={!hasAreas ? 'Debe crear al menos un área primero' : undefined}
            >
              {isMobile ? '' : 'Nueva Mesa'}
            </Button>
          )}
        </div>
      </div>

      {/* Card view */}
      {viewMode === 'cards' && (
        <Row className="table-grid-cards" gutter={isMobile ? [8, 8] : isTablet ? [16, 16] : [16, 16]}>
          {tables.map((table) => (
            <Col
              key={table.id}
              xs={12}
              sm={8}
              md={6}
              lg={6}
              xl={4}
              xxl={3}
            >
              <TableCard
                table={table}
                isSelected={selectedTableId === table.id}
                onClick={onTableSelect}
                onEdit={onTableEdit}
                onDelete={onTableDelete}
                onOpen={onTableOpen}
                onClose={onTableClose}
                onTransfer={onTableTransfer}
                onMerge={onTableMerge}
                onSplit={onTableSplit}
                onStatusChange={onTableStatusChange}
                showAreaName={showAreaColumn}
              />
            </Col>
          ))}
        </Row>
      )}

      {/* Compact view: one circle per table, grouped by area in "Todas" */}
      {viewMode === 'floor' && (
        <TableFloorView
          tables={tables}
          selectedTableId={selectedTableId}
          groupByArea={showAreaColumn}
          onSelect={onTableSelect}
          onOpen={onTableOpen}
          getMenuItems={getRowMenuItems}
        />
      )}

      {/* List view */}
      {viewMode === 'list' && (
        <AntTable
          dataSource={tables}
          columns={listColumns}
          rowKey="id"
          pagination={false}
          size={isMobile ? 'small' : 'middle'}
          onRow={(record) => ({
            onClick: () => handleRowClick(record),
            style: { cursor: 'pointer' },
            className: selectedTableId === record.id ? 'table-list-row-selected' : '',
          })}
          scroll={{ x: 'max-content' }}
        />
      )}
    </div>
  );
};

export default TableGrid;
